import React, {useMemo} from 'react'
import {Box, useRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import moduleContainerRecipe from './index.recipe'
import {hasValidString, sanitizeAnchorId} from '../../../utils/utils'

/**
 * A container component for CMS modules that:
 * - wraps content in a section element
 * - adds an optional anchor ID
 * - applies background color
 * - applies page container margins
 *
 * @component
 * @params {Object} props - Component properties.
 * @param {string} [props.anchor] - Optional ID for the section element to enable anchor linking.
 * @param {string} [props.background] - Background style for the container. One of 'default', 'subtle', 'strong', or 'inverse'.
 * @param {React.ReactNode} props.children - The content to be wrapped inside the section.
 * @returns {JSX.Element} A section element containing the provided content.
 */
const ModuleContainer = ({anchor, background = 'default', css, heading = {}, ...props}) => {
    const recipe = useRecipe({recipe: moduleContainerRecipe})
    const styles = useMemo(() => recipe({background}), [recipe, background])
    const sanitizedAnchor = useMemo(() => sanitizeAnchorId(anchor), [anchor])

    const {subline, headline1, headline2, variant} = heading

    const ariaLabel = useMemo(
        () =>
            [hasValidString(subline) && `${subline} -`, headline1, headline2]
                .filter(hasValidString)
                .join(' '),
        [subline, headline1, headline2]
    )

    const isAriaLabel = variant === 'hidden' && ariaLabel

    return (
        <Box css={styles}>
            <Box
                {...props}
                as="section"
                css={{
                    px: 'container-margin',
                    ...css
                }}
                id={sanitizedAnchor}
                layerStyle="container.page"
                aria-label={!isAriaLabel ? undefined : ariaLabel}
            />
        </Box>
    )
}

ModuleContainer.propTypes = {
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    css: PropTypes.object,
    heading: PropTypes.object
}

export default ModuleContainer
